import React, { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import "./CustomHLSPlayer.css";

const CustomHLSPlayer = ({ streamUrl }) => {
  const playerRef = useRef();
  const videoRef = useRef();
  const hlsRef = useRef();

  const [loading, setLoading] = useState(true);
  const [videoError, setVideoError] = useState(null);
  const [selectedQuality, setSelectedQuality] = useState("auto");
  // eslint-disable-next-line
  const [isFullscreen, setIsFullscreen] = useState(false);

  const qualityOptions = ["auto", "360p", "480p", "720p", "1080p"];

  const unavailableMessage =
    "This lecture video is currently unavailable. Please contact the support team.";

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "mozfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "MSFullscreenChange",
        handleFullscreenChange
      );
    };
  }, []);

  useEffect(() => {
    if (
      !streamUrl ||
      typeof streamUrl !== "string" ||
      streamUrl.trim() === ""
    ) {
      setVideoError("Invalid video stream URL.");
      setLoading(false);
      return;
    }

    let hls;
    setLoading(true);
    setVideoError(null);

    // ✅ New thing added: stop infinite loading if video is missing/not reachable
    const loadingTimeout = setTimeout(() => {
      setLoading(false);
      setVideoError(unavailableMessage);

      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    }, 15000);

    if (Hls.isSupported()) {
      // hls = new Hls();
      hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
        backBufferLength: 90,
        maxBufferLength: 60,
        maxMaxBufferLength: 120,
        startLevel: -1,
      });
      hlsRef.current = hls;

      hls.loadSource(streamUrl);
      hls.attachMedia(videoRef.current);

      // if (videoRef.current) videoRef.current.volume = 0.5;
      if (videoRef.current) videoRef.current.volume = 0.3;

      hls.on(Hls.Events.LEVEL_LOADED, () => {
        clearTimeout(loadingTimeout);
        setLoading(false);
      });

      hls.on(Hls.Events.ERROR, (_, data) => {
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              // ✅ Earlier it was retrying forever. Now we show proper message.
              clearTimeout(loadingTimeout);
              hls.destroy();
              hlsRef.current = null;
              setVideoError(unavailableMessage);
              setLoading(false);
              break;

            case Hls.ErrorTypes.MEDIA_ERROR:
              hls.recoverMediaError();
              break;

            default:
              clearTimeout(loadingTimeout);
              hls.destroy();
              hlsRef.current = null;
              setVideoError("Failed to load video.");
              setLoading(false);
              break;
          }
        }
      });

      return () => {
        clearTimeout(loadingTimeout);
        hls.destroy();
        hlsRef.current = null;
      };
    } else if (videoRef.current.canPlayType("application/vnd.apple.mpegurl")) {
      videoRef.current.src = streamUrl;
      videoRef.current.volume = 0.3;

      videoRef.current.onloadeddata = () => {
        clearTimeout(loadingTimeout);
        setLoading(false);
      };

      videoRef.current.onerror = () => {
        clearTimeout(loadingTimeout);
        setVideoError(unavailableMessage);
        setLoading(false);
      };
    } else {
      clearTimeout(loadingTimeout);
      setVideoError("This browser does not support HLS.");
      setLoading(false);
    }

    return () => {
      clearTimeout(loadingTimeout);
    };
  }, [streamUrl]);

  const handleQualityChange = (e) => {
    const selected = e.target.value;
    setSelectedQuality(selected);

    const qualityMap = {
      "360p": 0,
      "480p": 1,
      "720p": 2,
      "1080p": 3,
    };

    if (hlsRef.current) {
      if (selected === "auto") {
        hlsRef.current.currentLevel = -1;
      } else {
        const levelIndex = qualityMap[selected];
        hlsRef.current.currentLevel = levelIndex;
      }
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      playerRef.current.requestFullscreen?.() ||
        playerRef.current.webkitRequestFullscreen?.() ||
        playerRef.current.msRequestFullscreen?.();
    } else {
      document.exitFullscreen?.() ||
        document.webkitExitFullscreen?.() ||
        document.msExitFullscreen?.();
    }
  };

  return (
    <div ref={playerRef} className="custom-hls-player">
      {/* <video
        ref={videoRef}
        controls={!loading}
        onClick={toggleFullscreen}
        onLoadedData={() => setLoading(false)}
        style={{ width: "100%" }}
      /> */}

      <video
        ref={videoRef}
        controls={!loading}
        preload="metadata"
        playsInline
        onClick={toggleFullscreen}
        onLoadedData={() => setLoading(false)}
        style={{ width: "100%" }}
      />

      {loading && <div className="video-spinner"></div>}

      {!videoError && (
        <div className="quality-overlay">
          <span>Quality: {selectedQuality}</span>
          <select value={selectedQuality} onChange={handleQualityChange}>
            {qualityOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      )}

      {videoError && <div className="video-error">{videoError}</div>}
    </div>
  );
};

export default CustomHLSPlayer;